import { AppointmentStatusKey } from '../../helpers/appointments';
import { PaymentStatusKey } from '../../helpers/payments';

export type StatusChipTokenKey =
  | AppointmentStatusKey
  | `payment-${PaymentStatusKey}`;

type StatusChipGlyph = 'check' | 'slash' | 'none';

interface StatusChipRenderTraits {
  glyph: StatusChipGlyph;
  suppressDot: boolean;
  borderStyle: 'dashed' | 'solid';
  strikethrough: boolean;
}

const RENDER_TRAITS: Record<StatusChipTokenKey, StatusChipRenderTraits> = {
  unconfirmed: {
    glyph: 'none',
    suppressDot: false,
    borderStyle: 'dashed',
    strikethrough: false,
  },
  confirmed: {
    glyph: 'none',
    suppressDot: false,
    borderStyle: 'solid',
    strikethrough: false,
  },
  completed: {
    glyph: 'check',
    suppressDot: false,
    borderStyle: 'solid',
    strikethrough: false,
  },
  canceled: {
    glyph: 'none',
    suppressDot: true,
    borderStyle: 'solid',
    strikethrough: true,
  },
  noShow: {
    glyph: 'slash',
    suppressDot: false,
    borderStyle: 'solid',
    strikethrough: false,
  },
  'payment-succeeded': {
    glyph: 'check',
    suppressDot: false,
    borderStyle: 'solid',
    strikethrough: false,
  },
  'payment-forfeited': {
    glyph: 'none',
    suppressDot: false,
    borderStyle: 'solid',
    strikethrough: false,
  },
  'payment-refunded': {
    glyph: 'none',
    suppressDot: false,
    borderStyle: 'solid',
    strikethrough: false,
  },
};

interface Props {
  tokenKey: StatusChipTokenKey;
  size?: 'small' | 'medium' | 'detail';
  label: string;
}

function CheckIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M7.5 12.5l3 3 6-6" />
    </svg>
  );
}

function SlashIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={3}
      strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M6.5 17.5l11-11" />
    </svg>
  );
}

function StatusChipBase({ tokenKey, size = 'medium', label }: Props) {
  const dimensions =
    size === 'medium'
      ? {
          height: 28,
          paddingInline: 14,
          fontSize: 'body2.fontSize',
          gap: 7,
          dot: 8,
        }
      : size === 'detail'
      ? {
          height: 24,
          paddingInline: 11,
          fontSize: 'body2.fontSize',
          gap: 6,
          dot: 7,
        }
      : {
          height: 20,
          paddingInline: 9,
          fontSize: 'overline.fontSize',
          gap: 5,
          dot: 6,
        };

  const traits = RENDER_TRAITS[tokenKey];
  const cssVar = (suffix: 'fill' | 'border' | 'text' | 'accent') =>
    `var(--status-${tokenKey.toLowerCase()}-${suffix})`;

  const showGlyph = traits.glyph !== 'none' && size !== 'small';
  const showDot = !traits.suppressDot && !showGlyph;
  const accent = cssVar('accent');

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: dimensions.gap,
        height: dimensions.height,
        padding: `0 ${dimensions.paddingInline}px`,
        borderRadius: 50,
        background: cssVar('fill'),
        border: `1px ${traits.borderStyle} ${cssVar('border')}`,
        fontSize: dimensions.fontSize,
        fontWeight: 700,
        lineHeight: 'normal',
        color: cssVar('text'),
        textDecoration: traits.strikethrough ? 'line-through' : 'none',
        whiteSpace: 'nowrap',
      }}>
      {showDot && (
        <span
          style={{
            width: dimensions.dot,
            height: dimensions.dot,
            borderRadius: '50%',
            background: accent,
            flexShrink: 0,
          }}
        />
      )}
      {showGlyph && traits.glyph === 'check' && (
        <CheckIcon size={dimensions.dot + 4} color={accent} />
      )}
      {showGlyph && traits.glyph === 'slash' && (
        <SlashIcon size={dimensions.dot + 4} color={accent} />
      )}
      {label}
    </span>
  );
}

export default StatusChipBase;
